import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Skeleton, Typography, Grid, Tabs, Layout, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import fetchPatient from '../utils/patient/fetchPatient';
import fetchSessionsList from '../utils/session/fetchSessionsList';
import CallList from '../components/call/CallList';
import fetchCallsList from '../utils/call/fetchCallsList';
import BackLink from '../components/BackLink';
import SessionList from '../components/session/SessionList';
import fetchDocumentsList from '../utils/document/fetchDocumentsList';
import PatientDocumentList from '../components/document/PatientDocumentList';
import PatientDocumentViewer from '../components/document/PatientDocumentViewer';
import fetchDocument from '../utils/document/fetchDocument';
import { handleError } from '../utils/errorHandling';
import supportedFeature from '../utils/supportedFeature';
import PatientHeader from '../components/patient/PatientHeader';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Content } = Layout;

export default function PatientDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { patientUuid, documentUuid } = useParams();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const [sessions, setSessions] = useState([]);
  const [sessionPagination, setSessionPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [reloadSessionsTrigger, setReloadSessionsTrigger] = useState(0);

  const [documents, setDocuments] = useState([]);
  const [documentPagination, setDocumentPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [reloadDocumentsTrigger, setReloadDocumentsTrigger] = useState(0);
  const [openedDocument, setOpenedDocument] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  const [calls, setCalls] = useState([]);
  const [callPagination, setCallPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [reloadCallsTrigger, setReloadCallsTrigger] = useState(0);
  const [callsLoading, setCallsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('sessions');

  // Handle hash fragment on mount and changes
  useEffect(() => {
    const hash = location.hash?.replace('#', '').split('?')[0];
    // Only update if it's a valid tab
    if (['sessions', 'calls', 'documents'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Handle navigation state for session reload
  useEffect(() => {
    if (location.state?.reloadSessions) {
      handleSessionsReload();
      // Clear the state to prevent unnecessary reloads
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadPatient = async (patientUuid) => {
    try {
      const result = await fetchPatient(patientUuid);
      setPatient(result);
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    }
  };

  // Load patient either from patientUuid param or from document
  useEffect(() => {
    const loadPatientData = async () => {
      setLoading(true);
      try {
        if (documentUuid) {
          // If we're accessing via /documents/:documentUuid
          try {
            const document = await fetchDocument(documentUuid);
            setOpenedDocument(document);
            const patient = document.getPatient();
            if (!patient) {
              throw new Error('Patient not found');
            }
            setPatient(patient);
            setActiveTab('documents');
          } catch (error) {
            handleError(t('patient_details.error_loading'));
            navigate('/sessions#patients', { replace: true });
            return;
          }
        } else if (patientUuid) {
          // If we're accessing via /patients/:patientUuid
          await loadPatient(patientUuid);
        }
      } finally {
        setLoading(false);
      }
    };
    loadPatientData();
  }, [patientUuid, documentUuid]);

  useEffect(() => { loadSessions(sessionPagination.current); }, [patient?.getUUID(), reloadSessionsTrigger]);
  useEffect(() => { loadCalls(callPagination.current); }, [patient?.getUUID(), reloadCallsTrigger]);
  useEffect(() => { loadDocuments(documentPagination.current); }, [patient?.getUUID(), reloadDocumentsTrigger]);

  // When documents are reloaded (e.g., after polling finishes),
  // check if the currently opened document is among them and update it,
  // so that the viewer reflects the freshest version without manual reload.
  useEffect(() => {
    if (openedDocument && documents.length > 0) {
      const updatedDoc = documents.find(doc => doc.getUUID() === openedDocument.getUUID());
      if (updatedDoc) {
        setOpenedDocument(updatedDoc);
      } else {
        // Optional: if the document disappeared (deleted?), close drawer
        setOpenedDocument(null);
      }
    }
  }, [documents]);

  const loadSessions = async (page) => {
    if (!patient) return;
    setSessionsLoading(true);
    try {
      const { sessions, pagination } = await fetchSessionsList(page, { patient_uuid: patient.getUUID() });
      setSessions(sessions);
      setSessionPagination(pagination);
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadCalls = async (page) => {
    if (!patient) return;
    setCallsLoading(true);
    try {
      const { calls, pagination } = await fetchCallsList(page, { patient_uuid: patient.getUUID() });
      setCalls(calls);
      setCallPagination(pagination);
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    } finally {
      setCallsLoading(false);
    }
  };

  const loadDocuments = async (page) => {
    if (!patient) return;
    setDocumentsLoading(true);
    try {
      const { documents, pagination } = await fetchDocumentsList(page, { patient_uuid: patient.getUUID() });
      setDocuments(documents);
      setDocumentPagination(pagination);
      return documents;
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleTabChange = key => {
    setActiveTab(key);
    // Update URL hash without full navigation
    window.history.replaceState(null, '', `#${key}`);
  };

  const handleCallEnded = async () => {
    setActiveTab('calls');
    navigate(`/patients/${patient.getUUID()}#calls`, { replace: true });
    await new Promise(r => setTimeout(r, 1000));
    setReloadCallsTrigger(n => n + 1);
  };

  const handleDocumentsReload = async () => {
    setActiveTab('documents');
    navigate(`/patients/${patient.getUUID()}#documents`, { replace: true });
    setReloadDocumentsTrigger(n => n + 1);
  };

  const handleSessionsReload = () => {
    if (!patient) return;
    setActiveTab('sessions');
    navigate(`/patients/${patient.getUUID()}#sessions`, { replace: true });
    setReloadSessionsTrigger(n => n + 1);
  };

  const handleDocumentClick = (document) => {
    setOpenedDocument(document);
    // Use window.history.replaceState to update URL without navigation
    const newUrl = `/documents/${document.getUUID()}`;
    window.history.replaceState(null, '', newUrl);
  };

  const handleCloseDocument = () => {
    setOpenedDocument(null);
    // Navigate back to the patient page
    const newUrl = `/patients/${patient.getUUID()}`;
    window.history.replaceState(null, '', newUrl);
  };

  const handleGenerateDocumentAndOpen = async () => {
    setActiveTab('documents');
    navigate(`/patients/${patient.getUUID()}#documents`, { replace: true });

    const freshDocs = await loadDocuments(documentPagination.current);
    const latestDoc = freshDocs?.[0];
    if (latestDoc) {
      setOpenedDocument(latestDoc);
      // Use window.history.replaceState to update URL without navigation
      const newUrl = `/documents/${latestDoc.getUUID()}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  const renderSkeleton = () => (
    <div style={{ padding: '24px' }}>
      {[1, 2, 3].map((index) => (
        <Skeleton
          key={index}
          active
          avatar={false}
          paragraph={{ rows: 4 }}
          style={{ marginBottom: '16px' }}
        />
      ))}
    </div>
  );

  if (loading || !patient) {
    return (
      <Content className='page'>
        <div data-testid="patient-details-skeleton">
          <Skeleton 
            active 
            paragraph={{ rows: 4 }} 
          />
        </div>
      </Content>
    );
  }

  return (
    <div className="page patient-details-page">
      <div style={{ marginBottom: 16 }}>
        <BackLink to="/sessions#patients" />
      </div>

      <PatientHeader
        patient={patient}
        onCallEnded={handleCallEnded}
        onPatientUpdate={setPatient}
      />

      <Tabs
        type="card"
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginTop: 24 }}
        data-testid="patient-details-tabs"
        items={[
          {
            key: 'sessions',
            label: t('patient_details.sessions_tab'),
            'data-testid': 'sessions-tab',
            children: sessionsLoading ? (
              <div data-testid="sessions-skeleton">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ) : (
              <SessionList
                sessions={sessions}
                pagination={sessionPagination}
                showHeader
                fromPatientDetails
                onPageChange={loadSessions}
              />
            ),
          },
          {
            key: 'documents',
            label: t('patient_details.documents_tab'),
            'data-testid': 'documents-tab',
            children: documentsLoading ? (
              <div data-testid="documents-skeleton">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ) : (
              <PatientDocumentList
                patient={patient}
                documents={documents}
                pagination={documentPagination}
                onClick={handleDocumentClick}
                onGenerateClicked={handleGenerateDocumentAndOpen}
                onPageChange={loadDocuments}
              />
            ),
          },
          supportedFeature('Calls') && {
            key: 'calls',
            label: t('patient_details.calls_tab'),
            'data-testid': 'calls-tab',
            children: callsLoading ? (
              <div data-testid="calls-skeleton">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ) : (
              <CallList
                calls={calls}
                pagination={callPagination}
                fromPatientDetails
                onPageChange={loadCalls}
              />
            ),
          }
        ].filter(Boolean)}
      />

      <Drawer
        open={Boolean(openedDocument)}
        onClose={handleCloseDocument}
        placement="right"
        width={screens.xs ? '100%' : '65%'}
      >
        {openedDocument && (
          <PatientDocumentViewer
            document={openedDocument}
            onReloadDocuments={handleDocumentsReload}
          />
        )}
      </Drawer>
    </div>
  );
}